import React, { PropsWithChildren, ReactElement, useCallback } from 'react'
import FocusLock from 'react-focus-lock'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { setIsMobileNavMenuOpen } from 'store/dashboard/dashboard'
import Checklist from 'components/Dashboard/NavBar/Checklist'
import useGetUser from 'components/Dashboard/utils/useGetUser'
import { useShowChecklist } from 'components/Dashboard/utils/useShowChecklist'
import ModalDialog from 'ui/NewModalDialog'
import LogoMenu from './NavBarHeader/LogoMenu'
import CloseButton from 'ui/NewModalDialog/CloseButton'
import NavBarNotifications from './NavBarNotifications'
import { Flex } from 'theme-ui'
import useGetColorMode from 'utils/useGetColorMode'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'

const NavMenuMobile = ({ children }: PropsWithChildren): ReactElement => {
  const dispatch = useAppDispatch()
  const isMobileNavMenuOpen = useAppSelector(s => s.dashboard.isMobileNavMenuOpen)
  const { data: userData } = useGetUser({ refetchOnMountOrArgChange: true })
  const shouldShowChecklist = useShowChecklist(userData?.date)
  const { isLightMode } = useGetColorMode()
  const isMobile = useBreakpointIndex() === 0

  const onClose = useCallback(() => {
    dispatch(setIsMobileNavMenuOpen?.(false))
  }, [dispatch])

  return (
    <FocusLock autoFocus={false} disabled={!isMobileNavMenuOpen}>
      <ModalDialog
        isLeftContentVisible
        isRightContentVisible
        leftContent={
          <CloseButton
            onClick={onClose}
            sx={{
              svg: {
                width: '2.8rem',
                height: '2.8rem',
              },
            }}
          />
        }
        rightContent={<NavBarNotifications />}
        disableFocusLock={!isMobileNavMenuOpen}
        dataTestId="nav-menu-mobile"
        isOpen={isMobile && isMobileNavMenuOpen}
        onCloseClick={onClose}
        dismiss={onClose}
        elementInsteadOfTitle={<LogoMenu />}
        sxContentContainer={{
          backgroundColor: 'blue800',
        }}
        sxHeaderContainer={{
          borderBottom: 'none',
          boxShadow: ({ colors }): string => `0 1px 0 0 ${colors?.blueYonder30}`,
        }}
        sx={{
          '@keyframes slideInFromBottom': {
            '0%': {
              transform: 'translateY(100%)',
            },
            '100%': {
              transform: 'translateY(0)',
            },
          },

          animation: '0.2s ease-out 0s 1 slideInFromBottom',
        }}
      >
        <Flex
          sx={{
            position: 'relative',
            width: '100%',
            maxHeight: 'calc(100% - 0.2rem)',
            gap: '0.4rem',
            overflowY: 'auto',
            flexDirection: 'column',
            alignSelf: 'flex-start',
          }}
          className={`show-scrollbar ${isLightMode ? 'light' : 'dark'}`}
        >
          {shouldShowChecklist && <Checklist />}
          {children}
        </Flex>
      </ModalDialog>
    </FocusLock>
  )
}

export default NavMenuMobile
